import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Table } from 'react-bootstrap';
import PropTypes from 'prop-types';

import PageTitle from '../layouts/PageTitle';
import ModalConfirmation from './ModalConfirmation';
import SectionalCommittees from './SectionalCommittees';

import { useStaticCollections } from '../../context/StaticCollections';
import { useAdminCollections } from '../../context/AdminCollections';

import { calculateColumnWidths } from '../../utils/calculateColumnWidths';
import { calculateDynamicStyle } from '../../utils/calculateDynamicStyle';
import { handleConfirmSelect } from '../../utils/handleConfirmSelect';
import { arrayToString } from '../../utils/arrayToString';
import { renderUpdatedSpan } from '../../utils/renderUpdatedSpan';
import { NOMINATION_STATUS } from '../../utils/constants';

const TableNominationElect = ({
  nominationsInSectionalCommittee,
  handleSelectClick,
}) => {
  const { electionTypes, nominationStatusTypes } = useStaticCollections();
  const { candidates, nominationStateArray } = useAdminCollections();
  const cellRefs = useRef([]);
  const [columnWidths, setColumnWidths] = useState({});

  useEffect(() => {
    setColumnWidths(calculateColumnWidths(cellRefs.current));
  }, [nominationsInSectionalCommittee.length]);

  const sortedNominations = [...nominationsInSectionalCommittee].sort(
    (a, b) => {
      const stateA = nominationStateArray?.find(
        (state) => state.attributes?.nominationId === a.id,
      );
      const stateB = nominationStateArray?.find(
        (state) => state.attributes?.nominationId === b.id,
      );
      const rankA = stateA?.attributes?.ranking;
      const rankB = stateB?.attributes?.ranking;

      if (rankA != null && rankB != null) return rankA - rankB;
      if (rankA != null) return -1;
      if (rankB != null) return 1;
      return 0;
    },
  );

  return (
    <Card className="card-two mb-xl-5 table-responsive">
      <Card.Body>
        <Table bordered hover striped className="dataTable">
          <thead className="thead-dark">
            <tr>
              <th className="d-none">
                <b>Nomination Id</b>
              </th>
              <th>
                <b>Rank</b>
              </th>
              <th>
                <b>Name</b>
              </th>
              <th>
                <b>Type</b>
              </th>
              <th>
                <b>Descriptor</b>
              </th>
              <th>
                <b>Status</b>
              </th>
              <th className="text-center" aria-label="Actions">
                <b>Actions</b>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedNominations.map((nomination, index) => {
              const { nominationStatusTypeId } = nomination.attributes;
              const nominationState = nominationStateArray?.find(
                (state) => state.attributes?.nominationId === nomination.id,
              );
              const ranking = nominationState?.attributes?.ranking;
              const cells = [
                {
                  value: candidates[nomination.attributes.candidateId],
                },
                {
                  value: electionTypes[nomination.attributes.electionTypeId],
                },
                {
                  value: nomination.attributes.disciplineDescriptor
                    ? arrayToString(nomination.attributes.disciplineDescriptor)
                    : '',
                },
                {
                  value: nominationStatusTypes[nominationStatusTypeId],
                },
              ];

              return (
                <tr key={index}>
                  <td className="d-none">{nomination.id}</td>
                  <td className="text-center">
                    <strong>{ranking ?? ''}</strong>
                  </td>
                  {cells.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      ref={(el) => (cellRefs.current[cellIndex] = el)}
                      style={calculateDynamicStyle(columnWidths[cellIndex])}
                    >
                      {cell.value}{' '}
                      {cellIndex === 0 &&
                        renderUpdatedSpan(nomination?.attributes?.updatedAt)}
                    </td>
                  ))}
                  <td className="text-center">
                    {nominationStatusTypeId === NOMINATION_STATUS.ELECTED ? (
                      <i className="bi bi-check-square view-button" />
                    ) : (
                      <button
                        className="button-reset"
                        type="button"
                        aria-label="Select Candidate"
                        onClick={() =>
                          handleSelectClick(
                            `${nomination.id}`,
                            candidates[nomination.attributes.candidateId],
                          )
                        }
                      >
                        <span title="Elect Candidate">
                          <i className="bi bi-square action-button" />
                        </span>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

TableNominationElect.propTypes = {
  nominationsInSectionalCommittee: PropTypes.array.isRequired,
  handleSelectClick: PropTypes.func.isRequired,
};

const NominationElect = () => {
  const { refreshNominations, setRefreshNominations } = useAdminCollections();
  const [showModal, setShowModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedNominationId, setSelectedNominationId] = useState(null);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [electedCandidates, setElectedCandidates] = useState([]);
  const notificationTimerRef = useRef(null);
  const previousRefreshRef = useRef(refreshNominations);

  const handleSelectClick = (nominationId, candidate) => {
    setSelectedCandidate(candidate);
    setSelectedNominationId(nominationId);
    setShowModal(true);
  };

  const handleCancelSelect = () => {
    setShowModal(false);
  };

  const resetNotification = useCallback(() => {
    setNotificationTitle('');
    setNotificationMessage('');
    setElectedCandidates([]);
  }, []);

  useEffect(() => {
    const refreshChanged = previousRefreshRef.current !== refreshNominations;
    previousRefreshRef.current = refreshNominations;

    if (!showModal && refreshChanged && selectedCandidate) {
      setElectedCandidates((prev) => [...prev, selectedCandidate]);

      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
      }

      notificationTimerRef.current = setTimeout(() => {
        resetNotification();
      }, 5000);
    }
  }, [showModal, refreshNominations, selectedCandidate, resetNotification]);

  useEffect(() => {
    if (electedCandidates.length === 0) {
      return;
    }

    let candidateList;
    const [firstCandidate] = electedCandidates;

    if (electedCandidates.length === 1) {
      candidateList = firstCandidate;
    } else if (electedCandidates.length === 2) {
      const [first, second] = electedCandidates;
      candidateList = `${first} and ${second}`;
    } else {
      const allButLast = electedCandidates.slice(0, -1).join(', ');
      const lastCandidate = electedCandidates[electedCandidates.length - 1];
      candidateList = `${allButLast}, and ${lastCandidate}`;
    }

    const title =
      electedCandidates.length === 1
        ? 'Candidate Elected'
        : 'Candidates Elected';
    const verb = electedCandidates.length === 1 ? 'has' : 'have';

    setNotificationTitle(title);
    setNotificationMessage(
      `${candidateList} ${verb} been successfully elected.`,
    );
  }, [electedCandidates]);

  useEffect(
    () => () => {
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
      }
    },
    [],
  );

  return (
    <>
      <PageTitle
        motherMenu="Nominations"
        activeMenu="Elect candidate"
        notificationTitle={notificationTitle}
        notificationMessage={notificationMessage}
      />
      <SectionalCommittees
        TableComponent={TableNominationElect}
        statusTypes={[NOMINATION_STATUS.RECOMMENDED, NOMINATION_STATUS.ELECTED]}
        handleSelectClick={handleSelectClick}
      />
      <ModalConfirmation
        showModal={showModal}
        handleCancelSelect={handleCancelSelect}
        bodyText={`Are you sure you want to **elect** ${selectedCandidate}?`}
        handleConfirmSelect={handleConfirmSelect}
        rowId={selectedNominationId}
        apiIdentifier="nomination-elect"
        refreshCollections={refreshNominations}
        setRefreshCollections={setRefreshNominations}
        setShowModal={setShowModal}
      />
    </>
  );
};

export default NominationElect;
